import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { formatISO } from 'date-fns'
import { MessagesModel } from '../models/MessagesModel'
import { useUserStore } from './useUserStore'
import sanitizeHtml from 'sanitize-html'
import { isNullish, parseBool } from '../utils'
import { v4 as uuidv4 } from 'uuid'

export type Attachment = {
  id: string
  name: string
  link: string
}
export type Message = {
  id: number
  from: { name: string; username: string }
  to: { name: string; username: string }
  date: Date
  subject: string
  body: string
  read: boolean
  priority: boolean
  attachment: boolean
  attachments?: Attachment[]
  readDate?: Date | null
  updatedAt?: Date | null
  deletedAt?: Date
  uuid?: string
}

const allMessages = ref<Message[]>([])
const { writeMessages } = MessagesModel()
export const useMessages = defineStore('messages', () => {
  const { first_name, last_name, id: myId, username: myUserName } = storeToRefs(useUserStore())

  const myName = computed(() => `${first_name.value} ${last_name.value}`)
  const currentUser = computed(() => {
    return {
      id: myId.value,
      name: myName.value,
      username: myUserName.value,
      first_name: first_name.value,
      last_name: last_name.value,
    }
  })

  watch(
    allMessages,
    async (newData) => {
      if (skip.value) {
        skip.value = false
      } else {
        //write to device
        await writeMessages(newData).catch((error) => {
          console.log(error)
        })
      }
    },
    { deep: true }
  )
  const skip = ref(false)
  const setMessages = async (data: any[]) => {
    if (data.length > 0) {
      allMessages.value = data
      await writeMessages(allMessages.value)
    }
  }
  const addMessages = async (data: Message[]) => {
    const allMessagesIds = allMessages.value.map((message) => message.id)
    const newData: Message[] = data
      .filter((message) => {
        //only add if the message id doesn't exist already
        //this is a preventative measure against failed syncs.
        if (!allMessagesIds.includes(message.id)) return true
        else return false
      })
      .map((message: any) => {
        const {
          from_first_name,
          from_last_name,
          from_id,
          from_username,
          date,
          priority,
          read,
          ...rest
        } = message
        return {
          priority: parseBool(priority),
          date: new Date(date),
          from: {
            id: from_id,
            name: `${from_first_name} ${from_last_name}`,
            username: from_username,
            first_name: from_first_name,
            last_name: from_last_name,
          },
          to: currentUser.value,
          read: read,
          readDate: null,
          updatedAt: null,
          attachment: message.attachments.length ? true : false,
          ...rest,
        }
      })
    allMessages.value.push(...newData)

    await setMessages(allMessages.value)
  }

  const getMessagesInbox = computed(() =>
    allMessages.value
      .filter((message) => message.from.username !== myUserName.value && !message.deletedAt)
      .sort((a, b) => {
        return b.date.getTime() - a.date.getTime()
      })
  )
  const unread = computed((): number | null => {
    const count = getMessagesInbox.value.filter((message) => message.read === false).length
    return count > 0 ? count : null
  })
  const unreadUrgent = computed(() => {
    const count = getMessagesInbox.value.filter(
      (message) => message.read === false && message.priority === true
    ).length
    return count
  })
  const markRead = (data: Message['id']) => {
    const item = allMessages.value.find((message) => message.id === data)
    if (item && !item.read) {
      const date = new Date()
      item.read = true
      item.readDate = date
      item.updatedAt = date
    }
  }
  const message = (data: Message['id']): { message: Message; sent: boolean } => {
    const item = allMessages.value.find((message) => message.id == data)!
    return {
      message: item,
      sent: item?.from.username === myUserName.value ? true : false,
    }
  }
  const send = (data: Message): void => {
    allMessages.value.push({
      id: Math.floor(100000 + Math.random() * 900000),
      from: currentUser.value,
      to: data.to,
      date: new Date(),
      subject: data.subject,
      body: data.body,
      read: true,
      priority: false,
      attachment: false,
      uuid: uuidv4(),
    })
  }
  const deleteMessage = (data: Message['id']): void => {
    const index = allMessages.value.findIndex((message) => message.id === data)!
    allMessages.value[index].deletedAt = new Date()
  }
  /**Removes messages from device before a date
   * this would typically be the last sync date.
   */
  const removeMessagesDeleteBeforeDate = (date: Date): void => {
    allMessages.value = allMessages.value.filter((message) => {
      return !(!isNullish(message.deletedAt)
        ? message.deletedAt.getTime() <= date.getTime()
        : false)
    })
  }
  const getMessagesOutbox = computed(() =>
    allMessages.value
      .filter((message) => {
        return message.from.username === myUserName.value && !message.deletedAt
      })
      .sort((a, b) => {
        return b.date.getTime() - a.date.getTime()
      })
  )

  //Returns from outbox
  const getMessagesAfterDate = (date: Date) => {
    return getMessagesOutbox.value.filter((message) => {
      console.log({
        syncDate: date,
        messageDate: message.date,
        comparison: message.date.getTime() >= date.getTime(),
        valueMessageDate: message.date.getTime(),
        valuesyncDate: date.getTime(),
      })
      return message.date.getTime() >= date.getTime()
    })
  }

  //Returns from inbox
  const getReadReceiptsAfterDate = (date: Date) => {
    return getMessagesInbox.value
      .filter((message) => {
        if (message.read && message.readDate) {
          return message.readDate >= date
        } else {
          return false
        }
      })
      .map((message) => {
        return {
          id: message.id,
          read_date: formatISO(message.readDate!),
        }
      })
  }

  const getDeletedMessages = () => {
    return allMessages.value
      .filter((message) => {
        return message.deletedAt
      })
      .map((message) => {
        return {
          id: message.id,
          deleted_at: formatISO(message.deletedAt!),
        }
      })
  }

  const resetStoreState = () => {
    skip.value = true
    allMessages.value = []
  }

  return {
    resetStoreState,
    message,
    send,
    deleteMessage,
    unread,
    markRead,
    addMessages,
    setMessages,
    unreadUrgent,
    getMessagesInbox,
    getMessagesOutbox,
    getDeletedMessages,
    getMessagesAfterDate,
    getReadReceiptsAfterDate,
    removeMessagesDeleteBeforeDate,
  }
})

/**
 * Wrapper to sanitize messages
 * @param data string
 */
export const sanitizeMessage = (data: string) => {
  return sanitizeHtml(data, {
    allowedTags: [
      'img',
      'p',
      'blockquote',
      'pre',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'i',
      'b',
      'em',
      'strong',
      'del',
      'u',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'div',
      'sub',
      'sup',
      'a',
      'span',
      'ol',
      'ul',
      'li',
      'br',
      'pre',
    ],
    allowedAttributes: {
      a: ['href', 'name', 'target', 'style', 'class'],
      // We don't currently allow img itself by default, but
      // these attributes would make sense if we did.
      img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading'],
      span: ['style'],
      table: ['class'],
      ul: ['style', 'class'],
      ol: ['style', 'class'],
      li: ['style', 'class'],
    },
    allowedSchemesByTag: {
      img: ['data'],
    },
    transformTags: {
      a: sanitizeHtml.simpleTransform('a', {
        class: 'text-primary',
        style: 'text-decoration-line: underline;',
      }),
      ul: function () {
        return {
          tagName: 'ul',
          attribs: {
            style: 'list-style-type: disc; margin-left: 10px;',
          },
        }
      },
      ol: function () {
        return {
          tagName: 'ol',
          attribs: {
            style: 'list-style-type: decimal; margin-left: 10px;',
          },
        }
      },
      li: function () {
        return {
          tagName: 'li',
          attribs: {
            style: 'margin-left: 10px;',
          },
        }
      },
    },
  })
}
